import { useId } from 'react';

import { formatDateTime } from '@shared/format';
import { Alert } from '@shared/ui/Alert';
import { Dialog } from '@shared/ui/dialog';

import { copy } from './copy';
import type { GatewayTestResult } from '../_shared/types';

/**
 * The test transaction, and its outcome, in one place.
 *
 * **The result renders inside the dialog that launched it.** The sitemap carries
 * no result route, and a probe whose answer arrives on another surface is a
 * probe the operator has to go looking for.
 *
 * The result region is `aria-live="polite"`, **not** `role="alert"`: the
 * interrupting role is reserved for an error that arrives unrequested, and this
 * is the answer to a button the operator just pressed and is waiting on.
 *
 * The copy states what the test does **and does not** do — no entitlement
 * granted, no line in reporting — because a control that moves money is exactly
 * the one an operator hesitates over.
 */
export function TestDialog({
  open,
  busy,
  result,
  lastTestedAt,
  onRun,
  onClose,
}: {
  open: boolean;
  busy: boolean;
  /** `null` before the first press. A failing probe is a result, not an error. */
  result: GatewayTestResult | null;
  lastTestedAt: string | null;
  onRun: () => void;
  onClose: () => void;
}) {
  const headingId = useId();

  return (
    <Dialog open={open} labelledBy={headingId} onClose={onClose}>
      <form method="dialog" className="flex flex-col gap-4">
        <div>
          <h2 id={headingId} className="text-h3">
            {copy.test.title}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">{copy.test.lede}</p>
          {lastTestedAt ? (
            <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">
              {copy.test.lastTested(formatDateTime(lastTestedAt))}
            </p>
          ) : null}
        </div>

        <div aria-live="polite">
          {result ? (
            result.ok ? (
              <Alert kind="success" title={copy.test.successTitle}>
                {copy.test.successBody}
              </Alert>
            ) : (
              <Alert kind="danger" title={copy.test.failureTitle}>
                {copy.test.failureBody}
                {/*
                  The provider's own sentence is rendered as issued and isolated:
                  it is a foreign string in a foreign direction, and rewriting it
                  would hide the one detail that identifies the refusal.
                */}
                {result.provider_message ? (
                  <span className="mono mt-2 block text-xs">
                    <bdi>{result.provider_message}</bdi>
                  </span>
                ) : null}
              </Alert>
            )
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRun}
            disabled={busy}
            className="rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {busy ? copy.test.running : copy.test.run}
          </button>

          <button
            type="submit"
            value="close"
            formNoValidate
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {copy.test.close}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
